import { motion } from "motion/react";

const settle = { type: "spring", stiffness: 60, damping: 14 } as const;

/**
 * Two tiers, and the page must only ever use two.
 *
 * Before this existed every section shared ONE clamp, so a load-bearing pillar
 * (the notebook) and a utility block (How it works) rendered at identical size
 * — a skimmer got no ranking signal anywhere on the page.
 *
 *   - `pillar`  → the value props someone came for. Roughly 6 of these.
 *   - `utility` → supporting blocks: how it works, roadmap, FAQ.
 *
 * If a third tier ever feels necessary, the fix is almost always cutting a
 * section, not adding a size.
 */
type HeadingLevel = "pillar" | "utility";

const TITLE_SIZE: Record<HeadingLevel, string> = {
  pillar: "text-[clamp(30px,4vw,52px)]",
  utility: "text-[clamp(23px,2.2vw,32px)]",
};

const KICKER_SIZE: Record<HeadingLevel, string> = {
  pillar: "text-[13px]",
  utility: "text-[12px]",
};

interface SectionHeadingProps {
  kicker?: string;
  title: string;
  align?: "left" | "center";
  /** "panel" is for colored gradient panels — ink title, muted-ink kicker. */
  tone?: "ink" | "cream" | "panel";
  level?: HeadingLevel;
}

export function SectionHeading({
  kicker,
  title,
  align = "center",
  tone = "ink",
  level = "pillar",
}: SectionHeadingProps) {
  const alignClass = align === "center" ? "text-center" : "text-left";
  const titleColor = tone === "cream" ? "text-home-cream" : "text-home-ink";
  // ink/65 is the AA floor for text under 18.66px on cream-deep (4.84:1).
  const kickerColor =
    tone === "cream"
      ? "text-home-cream/75"
      : "text-home-ink/65";

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={settle}
      className={alignClass}
    >
      {kicker && (
        <p
          className={`mb-3 font-body ${KICKER_SIZE[level]} font-bold tracking-[0.16em] uppercase ${kickerColor}`}
        >
          {kicker}
        </p>
      )}
      <h2
        className={`font-display ${TITLE_SIZE[level]} leading-[1.05] font-semibold tracking-tight ${titleColor}`}
      >
        {title}
      </h2>
    </motion.div>
  );
}

export const SETTLE_SPRING = settle;
